<script setup lang="ts">
// Full-screen image viewer. Mounted once in AppShell; any view calls
// `useLightboxStore().open({...})` to expand an image. Click the scrim/padding
// or press Escape to close; a download button saves the original bytes
// (same-origin, cookie-auth, `download` attribute).
//
// Built on Reka's Dialog primitives so it inherits focus-trap, body scroll-lock,
// Escape, and return-focus - the manual keydown listener + body.style.overflow
// juggling the hand-rolled version carried are gone. The full-bleed chrome (scrim
// + centered image + toolbar) is ours, so we compose the raw primitives rather
// than the centered `Dialog` card.

import AppIcon from '@/components/AppIcon.vue';
import { computed } from 'vue';
import {
  DialogRoot, DialogPortal, DialogOverlay, DialogContent, DialogTitle,
  VisuallyHidden, Tooltip,
} from '@hq/chat-ui';

import { useLightboxStore } from '@/stores/lightbox';
import { useI18n } from '@/lib/i18n';

const { t } = useI18n();

const store = useLightboxStore();
const img = computed(() => store.active);

function close(): void { store.close(); }

// Reka requests close (Escape / the X / programmatic) via update:open(false).
function onOpenChange(open: boolean): void {
  if (!open) close();
}
</script>

<template>
  <DialogRoot :open="!!img" @update:open="onOpenChange">
    <DialogPortal>
      <DialogOverlay class="lightbox__overlay" />
      <DialogContent
        class="lightbox"
        :aria-describedby="undefined"
        @click.self="close"
      >
        <VisuallyHidden as-child>
          <DialogTitle>{{ t('lightbox.viewer_aria') }}</DialogTitle>
        </VisuallyHidden>
        <template v-if="img">
          <div class="lightbox__bar">
            <span class="lightbox__name">{{ img.filename ?? img.alt ?? '' }}</span>
            <Tooltip :label="t('lightbox.download')">
              <a
                v-if="img.downloadUrl ?? img.src"
                class="lightbox__btn"
                :href="img.downloadUrl ?? img.src"
                :download="img.filename ?? ''"
                :aria-label="t('lightbox.download_aria')"
                @click.stop
              >
                <AppIcon name="download-simple" :size="18" />
              </a>
            </Tooltip>
            <Tooltip :label="t('common.close')">
              <button
                type="button"
                class="lightbox__btn"
                :aria-label="t('common.close')"
                @click="close"
              >
                <AppIcon name="x" :size="18" />
              </button>
            </Tooltip>
          </div>
          <img
            :src="img.src"
            :alt="img.alt ?? ''"
            class="lightbox__img"
            @click.self="close"
          />
        </template>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<!-- Unscoped: overlay + content are portalled to <body> by Reka, so a scoped
     hash would never reach them. Classes are `lightbox`-prefixed. -->
<style>
.lightbox__overlay {
  position: fixed;
  inset: 0;
  z-index: 260;                 /* above modals (200) + toasts (250) */
  background: color-mix(in oklch, var(--n-950) 78%, transparent);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
.lightbox__overlay[data-state="open"]   { animation: lb-fade var(--dur-2, 0.18s) var(--ease-out, ease); }
.lightbox__overlay[data-state="closed"] { animation: lb-fade var(--dur-2, 0.18s) var(--ease-out, ease) reverse; }

.lightbox {
  position: fixed;
  inset: 0;
  z-index: 261;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  padding: var(--space-6);
}
.lightbox:focus-visible { outline: none; }

.lightbox__bar {
  position: absolute;
  top: var(--space-4);
  right: var(--space-4);
  left: var(--space-4);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}
.lightbox__name {
  flex: 1 1 auto;
  min-width: 0;
  color: oklch(0.97 0 0);
  font-size: var(--text-sm);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}
.lightbox__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  border: 1px solid oklch(1 0 0 / 0.18);
  border-radius: var(--radius-button);
  background: oklch(1 0 0 / 0.10);
  color: oklch(0.98 0 0);
  cursor: pointer;
  text-decoration: none;
  transition: background var(--dur-1) var(--ease-out), border-color var(--dur-1) var(--ease-out);
}
.lightbox__btn:hover { background: oklch(1 0 0 / 0.20); border-color: oklch(1 0 0 / 0.34); }
.lightbox__btn:focus-visible { outline: 2px solid oklch(0.98 0 0); outline-offset: 2px; }

.lightbox__img {
  max-width: min(92vw, 1400px);
  max-height: 88vh;
  object-fit: contain;
  border-radius: var(--radius-md);
  box-shadow: 0 24px 64px -12px rgba(0, 0, 0, 0.6);
  background: var(--surface-panel);
}
.lightbox[data-state="open"] .lightbox__img  { animation: lb-zoom var(--dur-3, 0.22s) var(--ease-out, ease); }

@keyframes lb-fade { from { opacity: 0; } }
@keyframes lb-zoom { from { opacity: 0; transform: scale(0.96); } }
@media (prefers-reduced-motion: reduce) {
  .lightbox__overlay[data-state],
  .lightbox[data-state] .lightbox__img { animation: none; }
}
</style>
